import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { EyeIcon, RefreshCw, SearchIcon, X } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@design-system';

import { cn } from '@/lib/utils';
import { setTableState } from '@/model/dataTableModel';
import { selectDataTable } from '..';

import { camelToCapitalized } from '@shared';

import { HorizontalScrollContainer } from './HorizontalScrollContainer';

import type {
  ColumnDef,
  ColumnFiltersState,
  ColumnPinningState,
  Table as ReactTable,
  Row,
  RowPinningState,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends unknown, TValue> {
    label?: string;
  }
}

interface RowMaskConfig<TData> {
  fn: (row: TData) => boolean;
  tooltipContent?: React.ReactNode;
}

interface DataTableProps<TData> {
  columns: ColumnDef<TData>[];
  getRowId: (row: TData) => string;
  data: TData[];
  tableId: string;
  globalSearchText?: string;
  globalSearchFilterKey?: string;
  globalSearchFieldKeys?: string[];
  globalSearchPlaceholder?: string;
  handleRefresh?: () => void;
  isLoading?: boolean;
  className?: string;
  initialColumnVisibility?: VisibilityState;
  initialColumnPinning?: ColumnPinningState;
  initialSorting?: SortingState;
  variant?: 'default' | 'outlined';
  isRowDeactivated?: (row: TData) => boolean;
  isRowMasked?: RowMaskConfig<TData>;
  neverMaskedColumns?: string[];
  summaryRowId?: string;
  footer?: React.ReactNode;
  primaryButton?: React.ReactNode;
  headerExtras?: React.ReactNode;
  tableSkeletonRows?: number;
  handleSearchTextChange?: (text: string) => void;
  defaultSelection?: TData[];
  pageSize?: number;
  hideColumnSelector?: boolean;
  onSelectionChange?: (selectedRows: TData[]) => void;
  useCustomScrollbar?: boolean;
  stackToolbarUntilDesktop?: boolean;
}

export function DataTable<TData>({
  columns,
  data,
  globalSearchText,
  globalSearchFilterKey,
  tableId,
  getRowId,
  globalSearchFieldKeys,
  globalSearchPlaceholder = 'Search by name or email...',
  handleRefresh,
  isLoading = false,
  className,
  initialColumnVisibility = {},
  initialColumnPinning = { left: [], right: [] },
  initialSorting = [],
  variant = 'default',
  isRowDeactivated,
  isRowMasked,
  neverMaskedColumns = [],
  footer,
  summaryRowId,
  primaryButton,
  headerExtras,
  handleSearchTextChange,
  tableSkeletonRows = 10,
  defaultSelection = [],
  pageSize = 25,
  hideColumnSelector = false,
  onSelectionChange,
  useCustomScrollbar = false,
  stackToolbarUntilDesktop = false,
}: DataTableProps<TData>) {
  const dispatch = useDispatch();
  const savedState = useSelector(selectDataTable)[tableId];

  const sortingStorageKey = `sorting:${tableId}`;
  const [sorting, setSorting] = useState<SortingState>(() => {
    const defaultSorting = savedState?.sorting || initialSorting;
    try {
      const stored = JSON.parse(localStorage.getItem(sortingStorageKey) || 'null');
      return stored && stored.id ? [stored] : defaultSorting;
    } catch {
      return defaultSorting;
    }
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    savedState?.columnFilters || [],
  );

  const columnVisibilityStorageKey = `columnVisibility:${tableId}`;
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => {
    const savedVisibility = savedState?.columnVisibility;
    const defaultVisibility =
      savedVisibility && typeof savedVisibility === 'object' && !Array.isArray(savedVisibility)
        ? { ...initialColumnVisibility, ...savedVisibility }
        : initialColumnVisibility;

    try {
      const stored = JSON.parse(localStorage.getItem(columnVisibilityStorageKey) || 'null');
      if (stored && typeof stored === 'object' && !Array.isArray(stored)) {
        return { ...defaultVisibility, ...stored };
      }
      return defaultVisibility;
    } catch {
      return defaultVisibility;
    }
  });
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(initialColumnPinning);

  const [rowPinning, _setRowPinning] = useState<RowPinningState>({
    top: [],
    bottom: summaryRowId ? [summaryRowId] : [],
  });

  const [rowSelection, setRowSelection] = useState(() => {
    const initialSelection: Record<string, boolean> = {};
    defaultSelection.forEach(row => {
      const rowId = getRowId(row);
      initialSelection[rowId] = true;
    });
    return initialSelection;
  });

  const searchFunction = useMemo(
    () => (row: any) => {
      // Early return if no search text
      if (!globalSearchText?.trim()) return true;

      // Normalize search value once
      const searchValue = globalSearchText.toLowerCase().trim();

      // Define searchable fields with fallbacks
      const fieldsToSearch = [
        { key: globalSearchFieldKeys?.[0] || 'clientName', fallback: '' },
        { key: 'email', fallback: '' },
      ];

      // Search through fields
      return fieldsToSearch.some(field => {
        try {
          const value = String(row.getValue(field.key) || field.fallback)
            .toLowerCase()
            .trim();

          // Split search terms for multi-word search
          const searchTerms = searchValue.split(/\s+/);
          return searchTerms.every(term => value.includes(term));
        } catch (error) {
          console.warn(`Search error for field ${field.key}:`, error);
          return false;
        }
      });
    },
    [globalSearchText, globalSearchFieldKeys],
  );

  const table = useReactTable({
    data,
    columns,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: updaterOrValue => {
      // onSortingChange accepts either a value or a function per TS types,
      // so we check both even though TanStack always passes a function.
      const nextSorting =
        typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;

      setSorting(nextSorting);
      localStorage.setItem(sortingStorageKey, JSON.stringify(nextSorting[0]));
    },
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: updaterOrValue => {
      // onColumnVisibilityChange accepts either a value or a function per TS types,
      // so we check both even though TanStack always passes a function.
      const nextVisibility =
        typeof updaterOrValue === 'function' ? updaterOrValue(columnVisibility) : updaterOrValue;

      setColumnVisibility(nextVisibility);
      localStorage.setItem(columnVisibilityStorageKey, JSON.stringify(nextVisibility));
    },
    onColumnPinningChange: setColumnPinning,
    onRowSelectionChange: setRowSelection,
    autoResetPageIndex: false,
    filterFns: {
      fuzzy: searchFunction,
    },
    globalFilterFn: 'fuzzy' as any,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      columnPinning,
      rowPinning,
      rowSelection,
    },
    initialState: {
      pagination: {
        pageSize: pageSize,
      },
      columnVisibility: initialColumnVisibility,
      columnPinning: initialColumnPinning,
    },
  });

  const filteredRowCount = table.getFilteredRowModel().rows.length;

  useEffect(() => {
    table.resetPageIndex();
  }, [filteredRowCount]);

  const paginationState = table.getState().pagination;

  useEffect(() => {
    dispatch(
      setTableState({
        tableId,
        tableState: {
          sorting,
          columnFilters,
          columnVisibility,
          pagination: paginationState,
        },
      }),
    );
  }, [dispatch, tableId, sorting, columnFilters, columnVisibility, paginationState]);

  useEffect(() => {
    table.setGlobalFilter(globalSearchText);
  }, [globalSearchText, table]);

  const totalRows = summaryRowId ? filteredRowCount - 1 : filteredRowCount;
  const currentPage = paginationState.pageIndex + 1;
  const firstRow = (currentPage - 1) * paginationState.pageSize + 1;
  const lastRow = Math.min(currentPage * paginationState.pageSize, totalRows);

  const tableContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (onSelectionChange) {
      const selectedRows = table.getSelectedRowModel().rows.map(row => row.original);
      onSelectionChange(selectedRows);
    }
  }, [rowSelection]);

  return (
    <div
      id={tableId}
      className={className}
    >
      <TableToolbar
        table={table}
        tableId={tableId}
        globalSearchText={globalSearchText}
        globalSearchFilterKey={globalSearchFilterKey}
        globalSearchPlaceholder={globalSearchPlaceholder}
        handleSearchTextChange={handleSearchTextChange}
        primaryButton={primaryButton}
        headerExtras={headerExtras}
        handleRefresh={handleRefresh}
        isLoading={isLoading}
        hideColumnSelector={hideColumnSelector}
        stackToolbarUntilDesktop={stackToolbarUntilDesktop}
      />

      <div>
        {isLoading ? (
          <TableSkeleton
            columns={Math.min(columns.length, 6)}
            variant={variant}
            rows={tableSkeletonRows ?? data.length}
          />
        ) : (
          <TableContent
            table={table}
            variant={variant}
            tableContainerRef={tableContainerRef}
            isRowDeactivated={isRowDeactivated}
            isRowMasked={isRowMasked}
            neverMaskedColumns={neverMaskedColumns}
            summaryRowId={summaryRowId}
            useCustomScrollbar={useCustomScrollbar}
          />
        )}
      </div>

      {footer && footer}

      <TablePagination
        table={table}
        totalRows={totalRows}
        firstRow={firstRow}
        lastRow={lastRow}
      />
    </div>
  );
}

function TableToolbar<TData>({
  table,
  tableId,
  globalSearchText,
  globalSearchFilterKey,
  globalSearchPlaceholder = 'Search by name or email...',
  handleSearchTextChange,
  primaryButton,
  headerExtras,
  handleRefresh,
  isLoading,
  hideColumnSelector,
  stackToolbarUntilDesktop,
}: {
  table: ReactTable<TData>;
  tableId: string;
  globalSearchText?: string;
  globalSearchFilterKey?: string;
  globalSearchPlaceholder?: string;
  handleSearchTextChange?: (text: string) => void;
  primaryButton?: React.ReactNode;
  headerExtras?: React.ReactNode;
  handleRefresh?: () => void;
  isLoading?: boolean;
  hideColumnSelector?: boolean;
  stackToolbarUntilDesktop?: boolean;
}) {
  return (
    <div
      className={cn(
        'mb-4 flex items-center gap-2',
        stackToolbarUntilDesktop &&
          'flex-col items-stretch xl:flex-row xl:items-center',
      )}
    >
      {/* Search bar */}
      {handleSearchTextChange && (
        <InputGroup
          className={cn(
            'h-10 w-full md:w-96',
            stackToolbarUntilDesktop && 'md:w-full xl:w-96',
          )}
        >
          <InputGroupAddon>
            <SearchIcon className="text-primary-700" />
          </InputGroupAddon>
          <InputGroupInput
            id="search-field"
            data-testid="search-field"
            name="table-search"
            autoComplete="off"
            value={globalSearchText}
            onChange={e => handleSearchTextChange(e.target.value)}
            placeholder={globalSearchPlaceholder}
          />
          {globalSearchText && (
            <InputGroupAddon align="inline-end">
              <InputGroupButton
                size="icon-xs"
                onClick={() => handleSearchTextChange('')}
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </InputGroupButton>
            </InputGroupAddon>
          )}
        </InputGroup>
      )}
      {/* Column selector and refresh button */}
      <div
        className={cn(
          'flex w-full justify-between gap-2',
          stackToolbarUntilDesktop && 'items-start',
        )}
      >
        {primaryButton && primaryButton}
        <div
          className={cn(
            'ml-auto flex items-center gap-2',
            stackToolbarUntilDesktop && 'min-w-0 flex-1 items-start justify-end',
          )}
        >
          {headerExtras &&
            (stackToolbarUntilDesktop ? (
              <div className="min-w-0 flex-1">{headerExtras}</div>
            ) : (
              headerExtras
            ))}
          {!hideColumnSelector && (
            <ColumnSelector
              table={table}
              tableId={tableId}
            />
          )}
          {handleRefresh && (
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={isLoading}
            >
              <RefreshCw className={`${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function ColumnSelector<TData>({ table, tableId }: { table: ReactTable<TData>; tableId: string }) {
  const anyColumnHidden = table
    .getAllColumns()
    .filter(col => col.getCanHide())
    .some(col => !col.getIsVisible());

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger>
        <Button
          variant="outline"
          id={`${tableId}__column-selector-trigger`}
          className={cn(anyColumnHidden && 'bg-primary/10 border-primary')}
          size="icon"
          aria-label="Show/Hide Columns"
        >
          <EyeIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="rounded-xl p-2"
      >
        {table
          .getAllColumns()
          .filter(column => column.getCanHide())
          .map(column => {
            return (
              <DropdownMenuCheckboxItem
                key={column.id}
                className=""
                checked={column.getIsVisible()}
                onCheckedChange={value => column.toggleVisibility(!!value)}
                data-column-id={column.id}
              >
                {column.columnDef.meta?.label ?? camelToCapitalized(column.id)}
              </DropdownMenuCheckboxItem>
            );
          })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function TableContent<TData>({
  table,
  variant,
  tableContainerRef,
  isRowDeactivated,
  isRowMasked,
  neverMaskedColumns = [],
  summaryRowId,
  useCustomScrollbar = false,
}: {
  table: ReactTable<TData>;
  variant: 'default' | 'outlined';
  tableContainerRef: React.RefObject<HTMLDivElement>;
  isRowDeactivated?: (row: TData) => boolean;
  isRowMasked?: RowMaskConfig<TData>;
  neverMaskedColumns: string[];
  summaryRowId?: string;
  useCustomScrollbar?: boolean;
}) {
  const tableContent = (
    <div className="inline-block min-w-full align-top">
      <Table className="relative w-full">
        <TableHeader
          className={`sticky top-0 z-10 ${
            variant === 'outlined' ? 'border-primary-200 bg-primary-50 border-b' : 'border-b-2'
          }`}
        >
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const pinningStyles: React.CSSProperties = header.column.getIsPinned()
                  ? {
                      position: 'sticky',
                      left:
                        header.column.getIsPinned() === 'left'
                          ? `${header.column.getStart()}px`
                          : undefined,
                      right:
                        header.column.getIsPinned() === 'right'
                          ? `${header.column.getStart()}px`
                          : undefined,
                      zIndex: 3,
                      backgroundColor: '#faf8fd',
                      boxShadow:
                        '2px 0 5px -2px rgba(0,0,0,0.1), inset -4px 1px 0px -2px rgba(0,0,0,0.1)',
                    }
                  : {};
                const columnSize = header.column.getSize();
                const headerStyles: React.CSSProperties = {
                  ...pinningStyles,
                  ...(columnSize > 0
                    ? { width: `${columnSize}px`, minWidth: `${columnSize}px` }
                    : {}),
                };
                return (
                  <TableHead
                    key={header.id}
                    className="group-hover:bg-primary-50 px-3"
                    style={headerStyles}
                    data-column-id={header.column.id}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getTopRows().map(row => (
            <PinnedRow
              key={row.id}
              row={row}
              table={table}
            />
          ))}
          {table.getCenterRows().map((row, index, array) => {
            const masked = isRowMasked?.fn(row.original);
            return (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
                className={cn(
                  'group dark:hover:bg-gray-800/50',
                  isRowDeactivated?.(row.original) && 'bg-primary-50 opacity-50',
                  summaryRowId !== undefined && index === array.length - 1 && 'border-b-2',
                )}
              >
                {row.getVisibleCells().map(cell => {
                  const isAlwaysVisible = neverMaskedColumns.includes(cell.column.id);
                  const pinningStyles: React.CSSProperties = cell.column.getIsPinned()
                    ? {
                        position: 'sticky',
                        left:
                          cell.column.getIsPinned() === 'left'
                            ? `${cell.column.getStart()}px`
                            : undefined,
                        right:
                          cell.column.getIsPinned() === 'right'
                            ? `${cell.column.getStart()}px`
                            : undefined,
                        zIndex: 2,
                        backgroundColor: 'white',
                        boxShadow:
                          '2px 0 5px -2px rgba(0,0,0,0.1), inset -4px 1px 0px -2px rgba(0,0,0,0.1)',
                      }
                    : {};
                  const columnSize = cell.column.getSize();
                  const cellStyles: React.CSSProperties = {
                    ...pinningStyles,
                    ...(columnSize > 0
                      ? { width: `${columnSize}px`, minWidth: `${columnSize}px` }
                      : {}),
                  };
                  return (
                    <TableCell
                      key={cell.id}
                      className="group-hover:bg-primary-50 p-3.5 text-gray-700"
                      style={cellStyles}
                      data-column-id={cell.column.id}
                    >
                      {masked && !isAlwaysVisible ? (
                        <MaskedCellContent tooltipContent={isRowMasked?.tooltipContent} />
                      ) : (
                        flexRender(cell.column.columnDef.cell, cell.getContext())
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
          {table.getBottomRows().map(row => (
            <PinnedRow
              key={row.id}
              row={row}
              table={table}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );

  if (useCustomScrollbar) {
    return (
      <div className="relative">
        <HorizontalScrollContainer
          className={`${variant === 'outlined' ? 'border-primary-200 rounded-lg border' : ''}`}
        >
          {tableContent}
        </HorizontalScrollContainer>
      </div>
    );
  }

  return (
    <div className="relative grid">
      <div
        ref={tableContainerRef}
        className={`overflow-x-auto ${
          variant === 'outlined' ? 'border-primary-200 rounded-lg border' : ''
        }`}
      >
        {tableContent}
      </div>
    </div>
  );
}

function MaskedCellContent({ tooltipContent }: { tooltipContent?: React.ReactNode }) {
  if (!tooltipContent) {
    return <span>---</span>;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help">---</span>
        </TooltipTrigger>
        <TooltipContent>{tooltipContent}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const PAGE_SIZE_OPTIONS = [25, 50, 100];

function TablePagination<TData>({
  table,
  totalRows,
  firstRow,
  lastRow,
}: {
  table: ReactTable<TData>;
  totalRows: number;
  firstRow: number;
  lastRow: number;
}) {
  const pageSize = table.getState().pagination.pageSize;
  const hasMultiplePages = totalRows > pageSize;

  if (!hasMultiplePages) {
    return null;
  }

  return (
    <div className="flex items-center justify-between gap-4 py-5">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="default"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="border-primary-300 text-primary-dark hover:bg-primary-100"
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="default"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="border-primary-300 text-primary-dark hover:bg-primary-100"
          >
            Next
          </Button>
        </div>
        <div className="hidden sm:block">
          <p className="text-sm text-gray-700">
            Showing <span className="font-semibold">{firstRow}</span> to{' '}
            <span className="font-semibold">{lastRow}</span> of{' '}
            <span className="font-semibold">{totalRows}</span> results
          </p>
        </div>
      </div>

      <div className="flex items-center gap-1">
        {PAGE_SIZE_OPTIONS.map(size => (
          <Button
            key={size}
            variant="outline"
            size="icon"
            onClick={() => {
              table.setPageSize(size);
              table.setPageIndex(0);
            }}
            className={cn(
              'h-9 w-9',
              size === pageSize
                ? 'bg-primary hover:bg-primary-hover border-primary text-white'
                : 'border-primary-300 text-primary-dark hover:bg-primary-100',
            )}
          >
            {size}
          </Button>
        ))}
        <span className="ml-1 text-sm text-gray-700">per page</span>
      </div>
    </div>
  );
}

function TableSkeleton({
  columns,
  rows = 10,
  variant = 'default',
}: {
  columns: number;
  rows?: number;
  variant?: 'default' | 'outlined';
}) {
  const columnWidths = Array.from({ length: columns }, () =>
    Math.floor(Math.random() * (150 - 75 + 1) + 75),
  );

  return (
    <div className={`${variant === 'outlined' ? 'border-primary-200 rounded-lg border' : ''}`}>
      <Table>
        <TableHeader
          className={`${
            variant === 'outlined' ? 'border-primary-200 bg-primary-50 border-b' : 'border-b-2'
          }`}
        >
          <TableRow>
            {columnWidths.map((width, index) => (
              <TableHead key={index}>
                <Skeleton
                  className="h-4"
                  style={{ width: `${width}px` }}
                />
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <TableRow
              key={rowIndex}
              className="hover:bg-primary-50"
            >
              {columnWidths.map((width, colIndex) => (
                <TableCell key={colIndex}>
                  <Skeleton
                    className="h-3"
                    style={{ width: `${width}px` }}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function PinnedRow({ row, table }: { row: Row<any>; table: ReactTable<any> }) {
  return (
    <TableRow
      className={cn(row.getIsPinned() === 'bottom' && 'border-primary-200 border-t-4')}
      style={{
        position: 'sticky',
        top: row.getIsPinned() === 'top' ? `${row.getPinnedIndex() * 26 + 48}px` : undefined,
        bottom:
          row.getIsPinned() === 'bottom'
            ? `${(table.getBottomRows().length - 1 - row.getPinnedIndex()) * 26}px`
            : undefined,
      }}
    >
      {row.getVisibleCells().map(cell => {
        const columnSize = cell.column.getSize();
        const cellStyles: React.CSSProperties =
          columnSize > 0 ? { width: `${columnSize}px`, minWidth: `${columnSize}px` } : {};
        return (
          <TableCell
            key={cell.id}
            className="px-3.5 py-4"
            style={cellStyles}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        );
      })}
    </TableRow>
  );
}
